import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import EventForm, { getAlarmSubtypes, SECOURS_SUBTYPES, getParisDateTimeLocal, CATEGORIES as EVENT_TYPE_CATEGORIES } from '@/components/EventForm';
import { getParisToday, CALENDAR_CHANGED_EVENT } from '@/components/CalendarWidget';
import { FormattedText, wrapTextareaSelection } from '@/lib/formattedText';
import FileViewer from '@/components/FileViewer';
import FileUpload from '@/components/FileUpload';
import SignatureDialog from '@/components/SignatureDialog';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Plus, FileText, Calendar, User, Download, Eye, Pencil, History, Paperclip, AlertTriangle, Info, KeyRound, CheckCircle2, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Libellés lisibles pour les champs additionnels (au lieu des noms techniques bruts)
const extraFieldLabels = {
  apprehension_number: "Numéro d'appréhension",
  amount: 'Montant',
  article_count: "Nombre d'articles",
  badge_number: 'Numéro de badge/clé',
  company_name: 'Société',
  technician_name: 'Technicien',
  phone_number: 'Téléphone',
  alarm_subtype: 'Sous-type',
  secours_subtype: 'Sous-type',
  category: 'Catégorie',
  event_datetime: 'Date et heure',
  event_type: "Type d'événement",
  attachments: 'Pièces jointes'
};

const formatExtraFieldValue = (key, value) => {
  if (key === 'amount' && value !== '' && value !== null && value !== undefined) {
    const n = parseFloat(value);
    return isNaN(n) ? value : `${n.toFixed(2)} €`;
  }
  if (key === 'event_datetime' && value) {
    return formatDateParis(value);
  }
  if (key === 'attachments') {
    // Anciennes entrées d'historique (avant l'ajout/retrait via la correction) :
    // une simple phrase ("3 pièce(s) jointe(s)..."), déjà lisible telle quelle.
    if (typeof value === 'string') return value;
    const count = Array.isArray(value) ? value.length : 0;
    return `${count} pièce${count > 1 ? 's' : ''} jointe${count > 1 ? 's' : ''}`;
  }
  return value;
};

// Affiche une date en heure de Paris, quel que soit le fuseau horaire de l'appareil
const formatDateParis = (isoString) => {
  const date = new Date(isoString);
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return `${parts.day} ${parts.month} ${parts.year} à ${parts.hour}:${parts.minute}`;
};

// Heure seule, en heure de Paris (utilisé par l'horodatage détaillé d'un événement)
const formatTimeParis = (isoString) => new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hour12: false
}).format(new Date(isoString));

// Date seule, en heure de Paris (utilisé par l'horodatage détaillé d'un événement)
const formatDateOnlyParis = (isoString) => new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris', day: '2-digit', month: 'long', year: 'numeric'
}).format(new Date(isoString));

// Convertit la valeur stockée d'un événement (déjà au format datetime-local,
// ou une chaîne ISO complète pour un ancien événement) vers le format attendu
// par un input datetime-local, en heure de Paris.
const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

// Date du jour (YYYY-MM-DD) en heure de Paris, indépendamment du fuseau de l'appareil
const getParisDateStr = (offsetDays = 0) => {
  const now = new Date();
  now.setDate(now.getDate() + offsetDays);
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Calcule les bornes de dates (start_date/end_date) à envoyer au serveur selon
// la période sélectionnée : Aujourd'hui, Hier, Cette semaine, ou personnalisée.
const getPeriodRange = (period, customStart, customEnd) => {
  if (period === 'today') {
    const d = getParisDateStr(0);
    return { start_date: `${d}T00:00:00`, end_date: `${d}T23:59:59` };
  }
  if (period === 'yesterday') {
    const d = getParisDateStr(-1);
    return { start_date: `${d}T00:00:00`, end_date: `${d}T23:59:59` };
  }
  if (period === 'week') {
    // Lundi de la semaine en cours, en heure de Paris
    const now = new Date();
    const parisDayStr = getParisDateStr(0);
    const parisDate = new Date(`${parisDayStr}T12:00:00`);
    const dayOfWeek = parisDate.getDay() === 0 ? 7 : parisDate.getDay(); // 1=lundi ... 7=dimanche
    const mondayOffset = -(dayOfWeek - 1);
    const monday = getParisDateStr(mondayOffset);
    const today = getParisDateStr(0);
    return { start_date: `${monday}T00:00:00`, end_date: `${today}T23:59:59` };
  }
  if (period === 'custom' && customStart && customEnd) {
    return { start_date: `${customStart}T00:00:00`, end_date: `${customEnd}T23:59:59` };
  }
  return { start_date: null, end_date: null };
};

const CATEGORIES = ['Général', 'Sûreté', 'Incendie', 'Secours à personnes', 'Technique', 'Divers'];

const categoryColors = {
  'Général': 'bg-gray-500',
  'Sûreté': 'bg-blue-500',
  'Incendie': 'bg-red-500',
  'Secours à personnes': 'bg-green-500',
  'Technique': 'bg-yellow-500',
  'Divers': 'bg-purple-500'
};

const MainCourante = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [sites, setSites] = useState([]);
  const [activeTemporaryConsignes, setActiveTemporaryConsignes] = useState([]);
  const [todayCalendarEvents, setTodayCalendarEvents] = useState([]);
  const [viewingTemporaryConsigne, setViewingTemporaryConsigne] = useState(null);
  const [showingTemporaryList, setShowingTemporaryList] = useState(false);
  const [viewingCalendarEvent, setViewingCalendarEvent] = useState(null);
  const [signingTemporaryConsigne, setSigningTemporaryConsigne] = useState(null);
  // Signature depuis un compte de site : l'agent signataire doit être
  // sélectionné et confirmer son identité avec son code personnel avant de
  // pouvoir dessiner sa signature (même logique que pour les consignes
  // permanentes, voir Consignes.js).
  const isSiteAccount = user?.role === 'site';
  const [consigneSignerAgents, setConsigneSignerAgents] = useState([]);
  const [consigneSignerId, setConsigneSignerId] = useState('');
  const [consigneSignerPin, setConsigneSignerPin] = useState('');
  const [consigneSignerPinVerified, setConsigneSignerPinVerified] = useState(false);
  const [consigneSignerPinChecking, setConsigneSignerPinChecking] = useState(false);
  const [consigneSignerPinError, setConsigneSignerPinError] = useState('');
  const [consigneSignerPinDialogOpen, setConsigneSignerPinDialogOpen] = useState(false);

  useEffect(() => {
    setConsigneSignerId('');
    setConsigneSignerPin('');
    setConsigneSignerPinVerified(false);
    setConsigneSignerPinError('');
    if (!isSiteAccount || !signingTemporaryConsigne) { setConsigneSignerAgents([]); return; }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${signingTemporaryConsigne.site_id}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setConsigneSignerAgents(await res.json());
      } catch { /* silencieux */ }
    })();
    // eslint-disable-next-line
  }, [signingTemporaryConsigne]);

  const verifyConsigneSignerPin = async (pin, agentId) => {
    setConsigneSignerPinChecking(true);
    setConsigneSignerPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/verify-redacteur-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ redacteur_id: agentId, pin })
      });
      if (res.ok) {
        setConsigneSignerPinVerified(true);
        setConsigneSignerPinDialogOpen(false);
      } else {
        const err = await res.json();
        setConsigneSignerPinError(err.detail || 'Code incorrect');
        setConsigneSignerPinVerified(false);
        setConsigneSignerPin('');
      }
    } catch {
      setConsigneSignerPinError('Erreur de connexion, merci de réessayer');
      setConsigneSignerPinVerified(false);
    } finally {
      setConsigneSignerPinChecking(false);
    }
  };
  // Suivi des clés perçues et non rendues (voir GET /api/keys/pending-status) :
  // bandeau jaune à partir de 17h, rouge à partir de 18h, avec une popup de
  // rappel une fois par palier tant qu'elle n'a pas été validée.
  const [keyAlertStatus, setKeyAlertStatus] = useState(null);
  const [showKeyAlertList, setShowKeyAlertList] = useState(false);
  const [showKeyAlertPopup, setShowKeyAlertPopup] = useState(false);
  const [keyObservationText, setKeyObservationText] = useState({});
  const [submittingKeyObservation, setSubmittingKeyObservation] = useState(null);
  const [acknowledgingKeyReturn, setAcknowledgingKeyReturn] = useState(null);
  // Validation de la popup de rappel : la personne qui valide doit être
  // identifiée (utile sur un compte de site partagé) et confirmer avec son
  // code personnel - l'historique gardera trace de qui a validé pour tout le monde.
  const [keySiteAgents, setKeySiteAgents] = useState([]);
  const [keyPopupValidatorId, setKeyPopupValidatorId] = useState('');
  const [keyPopupPin, setKeyPopupPin] = useState('');
  const [keyPopupPinChecking, setKeyPopupPinChecking] = useState(false);
  const [keyPopupPinError, setKeyPopupPinError] = useState('');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [correctingEvent, setCorrectingEvent] = useState(null);
  const [viewingAttachments, setViewingAttachments] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(null);
  const [viewingTimestampFor, setViewingTimestampFor] = useState(null);
  const [correctionText, setCorrectionText] = useState('');
  const correctionTextareaRef = useRef(null);
  // Champs complémentaires modifiables lors d'une correction (sous-type,
  // numéro d'appréhension, montant, nombre d'articles, numéro de badge/clé...),
  // selon la catégorie/le type de l'événement corrigé.
  const [correctionExtraFields, setCorrectionExtraFields] = useState({});
  const [correctionCategory, setCorrectionCategory] = useState('');
  const [correctionEventType, setCorrectionEventType] = useState('');
  const [correctionDatetime, setCorrectionDatetime] = useState('');
  // Pièces jointes de l'événement en cours de correction : les ids retirés
  // parmi celles déjà présentes (FileViewer) et les ids ajoutés durant cette
  // correction (FileUpload) - combinés à la soumission pour obtenir la
  // liste cible complète (voir handleSubmitCorrection).
  const [correctionRemovedAttachments, setCorrectionRemovedAttachments] = useState([]);
  const [correctionNewAttachments, setCorrectionNewAttachments] = useState([]);
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  // Sur un compte de site, corriger un événement rédigé par un agent exige de
  // resaisir le code personnel de ce rédacteur (celui de l'événement, pas un
  // autre), vérifié en direct comme à la création.
  const [correctionPin, setCorrectionPin] = useState('');
  const [correctionPinVerified, setCorrectionPinVerified] = useState(false);
  const [correctionPinChecking, setCorrectionPinChecking] = useState(false);
  const [correctionPinError, setCorrectionPinError] = useState('');
  const [correctionPinDialogOpen, setCorrectionPinDialogOpen] = useState(false);
  const correctionNeedsPin = user.role === 'site' && !!correctingEvent?.redacteur_id;
  // Quels champs complémentaires sont modifiables, basés sur la catégorie et
  // le type en cours d'édition (pas ceux d'origine) : si l'utilisateur change
  // de catégorie et/ou de type, les champs affichés doivent suivre en direct.
  const correctionSelectedCategory = EVENT_TYPE_CATEGORIES.find(c => c.value === correctionCategory);
  const correctionIsApprehension = correctionEventType === 'Appréhension';
  const correctionIsBadgeOrKey = ['Départ badge', 'Retour badge', 'Départ clé', 'Retour clé'].includes(correctionEventType);
  const correctionIsKeyDeparture = correctionEventType === 'Départ clé';
  const correctionIsAlarm = ['Sûreté', 'Incendie'].includes(correctionCategory) && correctionEventType === 'Alarme';
  const correctionIsSecours = correctionCategory === 'Secours à personnes' && ['Client', 'Employé'].includes(correctionEventType);
  const canCorrectEvent = (event) => {
    const canCorrectOthers = user.permission_overrides?.correct_others_events ?? (user.role === 'admin' || user.role === 'chef_poste');
    const canCorrectOwn = user.permission_overrides?.correct_own_events ?? true;
    return canCorrectOthers || (event.user_id === user.id && canCorrectOwn);
  };

  const canMarkImportant = user.role === 'admin' || user.role === 'chef_poste';
  // Observations ajoutées sous la description d'un événement, réservé aux
  // chefs de poste et admins (même autorisation que "marquer important").
  const canAddObservation = canMarkImportant;
  const [addingObservationFor, setAddingObservationFor] = useState(null);
  const [observationDrafts, setObservationDrafts] = useState({});
  const [submittingObservationFor, setSubmittingObservationFor] = useState(null);

  // Export Excel de la période personnalisée (déplacé depuis le tableau de
  // bord, qui exportait uniquement par mois calendaire) : exporte les
  // événements de la main courante entre customStart et customEnd, filtrés
  // sur le site actuellement sélectionné.
  const canExportExcel = user.role === 'admin' || user.permission_overrides?.export_monthly_data;
  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportExcel = async () => {
    if (!customStart || !customEnd) return;
    const token = localStorage.getItem('token');
    setExportingExcel(true);
    try {
      const params = new URLSearchParams({ start_date: customStart, end_date: customEnd, format: 'xlsx' });
      if (selectedSite !== 'all') params.append('site_id', selectedSite);
      const response = await fetch(`${BACKEND_URL}/api/reports/monthly-export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lors de l'export");
        return;
      }
      const blob = await response.blob();
      if (blob.size === 0) {
        toast.warning('Aucune donnée à exporter pour cette période');
        return;
      }
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `rapport_main_courante_${customStart}_au_${customEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export Excel téléchargé avec succès');
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleMarkImportant = async (eventId) => {
    if (!window.confirm('Marquer cet événement comme important ? Un email sera envoyé aux clients de ce site.')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/mark-important`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Événement marqué important, clients notifiés par email');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleValidateEvent = async (eventId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/validate`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Événement validé');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleAddObservation = async (eventId) => {
    const text = (observationDrafts[eventId] || '').trim();
    if (!text) {
      toast.error('L\'observation ne peut pas être vide');
      return;
    }
    setSubmittingObservationFor(eventId);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/observations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      if (response.ok) {
        toast.success('Observation ajoutée');
        setObservationDrafts(prev => ({ ...prev, [eventId]: '' }));
        setAddingObservationFor(null);
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de l\'ajout de l\'observation');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSubmittingObservationFor(null);
    }
  };

  useEffect(() => {
    loadData();
    // Actualise automatiquement la liste toutes les 10 secondes, pour que les
    // événements créés par d'autres utilisateurs apparaissent sans avoir
    // besoin de rafraîchir la page manuellement.
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedSite, selectedCategory, selectedEventType, periodFilter, customStart, customEnd]);

  useEffect(() => {
    loadActiveTemporaryConsignes();
    // Réactualise régulièrement pour que l'encadré disparaisse tout seul dès
    // qu'une consigne temporaire expire, sans avoir besoin de rafraîchir la page.
    const interval = setInterval(loadActiveTemporaryConsignes, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedSite]);

  const loadActiveTemporaryConsignes = async () => {
    const token = localStorage.getItem('token');
    try {
      const params = selectedSite !== 'all' ? `?site_id=${selectedSite}` : '';
      const res = await fetch(`${BACKEND_URL}/api/consignes/active-events${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setActiveTemporaryConsignes(await res.json());
    } catch { /* silencieux, non bloquant */ }
  };

  // Rappel discret des événements du calendrier prévus aujourd'hui sur ce
  // site, pour que l'info soit visible directement depuis la main courante
  // sans avoir à ouvrir le calendrier.
  useEffect(() => {
    loadTodayCalendarEvents();
    window.addEventListener(CALENDAR_CHANGED_EVENT, loadTodayCalendarEvents);
    return () => window.removeEventListener(CALENDAR_CHANGED_EVENT, loadTodayCalendarEvents);
    // eslint-disable-next-line
  }, [selectedSite]);

  const loadTodayCalendarEvents = async () => {
    const token = localStorage.getItem('token');
    try {
      const todayStr = getParisToday();
      const params = new URLSearchParams({ month: todayStr.slice(0, 7) });
      if (selectedSite !== 'all') params.append('site_id', selectedSite);
      const res = await fetch(`${BACKEND_URL}/api/calendar-events?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTodayCalendarEvents(data.filter((ev) => ev.date === todayStr));
      }
    } catch { /* silencieux, non bloquant */ }
  };

  // Le suivi des clés est propre à un site précis : pas d'affichage tant que
  // "Tous les sites" est sélectionné (vue d'ensemble multi-sites, admin).
  useEffect(() => {
    if (selectedSite === 'all') {
      setKeyAlertStatus(null);
      return undefined;
    }
    loadKeyAlertStatus();
    const interval = setInterval(loadKeyAlertStatus, 60000);

    // Revérifie pile à 17h00 et 18h00 (heure de Paris) pour que le bandeau
    // change de couleur et que la popup de rappel apparaisse sans attendre le
    // prochain intervalle de 60s ni un rechargement de page.
    const timers = [];
    const scheduleNext = (hour, minute) => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);
      if (next <= now) next.setDate(next.getDate() + 1);
      const timer = setTimeout(async () => {
        await loadKeyAlertStatus();
        scheduleNext(hour, minute);
      }, next.getTime() - now.getTime());
      timers.push(timer);
    };
    scheduleNext(17, 0);
    scheduleNext(18, 0);

    return () => { clearInterval(interval); timers.forEach(clearTimeout); };
    // eslint-disable-next-line
  }, [selectedSite]);

  const loadKeyAlertStatus = async () => {
    if (selectedSite === 'all') return;
    const token = localStorage.getItem('token');
    try {
      const [statusRes, agentsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/keys/pending-status?site_id=${selectedSite}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/sites/${selectedSite}/agents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (agentsRes.ok) setKeySiteAgents(await agentsRes.json());
      if (statusRes.ok) {
        const data = await statusRes.json();
        setKeyAlertStatus(data);
        const alreadyAcked = data.alert_level === 'yellow' ? data.popup_ack_yellow : data.popup_ack_red;
        const shouldShow = data.alert_level !== 'none' && !alreadyAcked;
        setShowKeyAlertPopup(shouldShow);
        if (!shouldShow) {
          setKeyPopupValidatorId('');
          setKeyPopupPin('');
          setKeyPopupPinError('');
        }
      }
    } catch { /* silencieux, non bloquant */ }
  };

  const handleAckKeyPopup = async (pin) => {
    if (!keyAlertStatus || keyAlertStatus.alert_level === 'none' || !keyPopupValidatorId) return;
    setKeyPopupPinChecking(true);
    setKeyPopupPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/keys/acknowledge-popup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSite, bucket: keyAlertStatus.alert_level, validator_id: keyPopupValidatorId, pin })
      });
      if (res.ok) {
        // Ne ferme la popup que si la validation a bien été enregistrée -
        // sinon elle réapparaîtrait aussitôt au prochain rechargement du
        // statut, sans que l'utilisateur ne comprenne pourquoi.
        setShowKeyAlertPopup(false);
        loadKeyAlertStatus();
      } else {
        const err = await res.json();
        setKeyPopupPinError(err.detail || 'Code incorrect');
        setKeyPopupPin('');
      }
    } catch {
      setKeyPopupPinError('Erreur de connexion, merci de réessayer');
    } finally {
      setKeyPopupPinChecking(false);
    }
  };

  const handleAddKeyObservation = async (eventId) => {
    const text = (keyObservationText[eventId] || '').trim();
    if (!text) return;
    setSubmittingKeyObservation(eventId);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${eventId}/observations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      if (res.ok) {
        toast.success('Observation ajoutée');
        setKeyObservationText((prev) => ({ ...prev, [eventId]: '' }));
        loadKeyAlertStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'ajout de l\'observation');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSubmittingKeyObservation(null);
    }
  };

  const handleAcknowledgeForgottenKeyReturn = async (departureEventId) => {
    if (!window.confirm('Confirmer que cette clé a bien été rendue, alors que personne n\'a saisi de "Retour clé" ? Aucun événement ne sera créé, la clé sera simplement retirée du suivi.')) return;
    setAcknowledgingKeyReturn(departureEventId);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/keys/${departureEventId}/acknowledge-forgotten-return`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Retour de clé acquitté');
        setShowKeyAlertList(false);
        loadKeyAlertStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'acquittement');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setAcknowledgingKeyReturn(null);
    }
  };

  const handleSignTemporaryConsigne = async (signatureData) => {
    if (!signingTemporaryConsigne) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/consignes/${signingTemporaryConsigne.id}/sign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_data: signatureData,
          ...(isSiteAccount ? { redacteur_id: consigneSignerId, redacteur_pin: consigneSignerPin } : {})
        })
      });
      if (response.ok) {
        toast.success('Consigne signée');
        setSigningTemporaryConsigne(null);
        setViewingTemporaryConsigne(null);
        loadActiveTemporaryConsignes();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la signature');
        if (isSiteAccount) {
          setConsigneSignerPin('');
          setConsigneSignerPinVerified(false);
        }
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const loadData = async () => {
    const token = localStorage.getItem('token');
    // Tant que la période personnalisée n'est pas complète, on n'affiche rien
    // plutôt que de charger tous les événements par défaut.
    if (periodFilter === 'custom' && (!customStart || !customEnd)) {
      setEvents([]);
      setLoading(false);
      return;
    }
    try {
      const { start_date, end_date } = getPeriodRange(periodFilter, customStart, customEnd);
      const params = new URLSearchParams();
      if (selectedSite !== 'all') params.append('site_id', selectedSite);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedCategory !== 'all' && selectedEventType !== 'all') params.append('event_type', selectedEventType);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const [eventsRes, sitesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/events?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/sites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData);
      }

      if (sitesRes.ok) {
        const sitesData = await sitesRes.json();
        setSites(sitesData);
        // Un non-admin n'a pas la possibilité de choisir "Tous les sites" :
        // son site (unique, ou celui choisi à la connexion) est sélectionné directement.
        if (user?.role !== 'admin' && sitesData.length > 0) {
          setSelectedSite(prev => (prev === 'all' ? sitesData[0].id : prev));
        }
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEventCreated = () => {
    setShowForm(false);
    loadData();
    toast.success('Événement créé avec succès');
  };

  // Suppression d'une pièce jointe (réservée aux admins par défaut, voir
  // delete_event_attachments) - contrairement à un événement, une pièce
  // jointe erronée peut être retirée ; la suppression est tracée dans
  // l'historique de l'événement côté serveur.
  const handleDeleteAttachment = async (eventId, fileId) => {
    if (!window.confirm('Supprimer définitivement cette pièce jointe ?')) return false;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${eventId}/attachments/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, attachments: updated.attachments, edit_history: updated.edit_history } : e)));
        toast.success('Pièce jointe supprimée');
        return true;
      }
      const err = await res.json();
      toast.error(err.detail || 'Erreur lors de la suppression');
      return false;
    } catch {
      toast.error('Erreur de connexion');
      return false;
    }
  };

  const openCorrection = (event) => {
    setCorrectingEvent(event);
    setCorrectionText(event.description);
    setCorrectionExtraFields(event.extra_fields || {});
    setCorrectionCategory(event.category);
    setCorrectionEventType(event.event_type);
    setCorrectionDatetime(toDateTimeLocalValue(event.event_datetime));
    setCorrectionRemovedAttachments([]);
    setCorrectionNewAttachments([]);
    setCorrectionPin('');
    setCorrectionPinVerified(false);
    setCorrectionPinError('');
    // Sur un compte de site, la popup de saisie du code personnel s'ouvre
    // automatiquement juste après l'ouverture de la fenêtre de correction.
    if (user.role === 'site' && event.redacteur_id) {
      setCorrectionPinDialogOpen(true);
    }
  };

  const verifyCorrectionPin = async (pin) => {
    setCorrectionPinChecking(true);
    setCorrectionPinError('');
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/verify-redacteur-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ redacteur_id: correctingEvent.redacteur_id, pin })
      });
      if (response.ok) {
        setCorrectionPinVerified(true);
        setCorrectionPinDialogOpen(false);
      } else {
        const err = await response.json();
        setCorrectionPinError(err.detail || 'Code incorrect');
        setCorrectionPinVerified(false);
        setCorrectionPin('');
      }
    } catch {
      setCorrectionPinError('Erreur de connexion, merci de réessayer');
      setCorrectionPinVerified(false);
    } finally {
      setCorrectionPinChecking(false);
    }
  };

  const handleSubmitCorrection = async () => {
    if (!correctionText.trim()) {
      toast.error('La description ne peut pas être vide');
      return;
    }
    if (user?.role !== 'admin' && correctionDatetime && new Date(correctionDatetime) > new Date(Date.now() + 5 * 60000)) {
      toast.error('Impossible de dater un événement de plus de 5 minutes dans le futur');
      return;
    }
    if (correctionIsAlarm && !correctionExtraFields.alarm_subtype) {
      toast.error('Le sous-type de l\'alarme est requis pour cette catégorie');
      return;
    }
    if (correctionIsSecours && !correctionExtraFields.secours_subtype) {
      toast.error('Le sous-type (Malaise/Blessure) est requis pour cette catégorie');
      return;
    }
    if (correctionIsKeyDeparture && !correctionExtraFields.departure_final
      && (!correctionExtraFields.alert_time_1 || !correctionExtraFields.alert_time_2)) {
      toast.error('Les deux horaires de rappel sont obligatoires, sauf en cas de départ définitif');
      return;
    }
    if (!correctionEventType) {
      toast.error('Le type d\'événement est obligatoire');
      return;
    }
    const descriptionChanged = correctionText.trim() !== correctingEvent.description;
    const extraFieldsChanged = JSON.stringify(correctionExtraFields) !== JSON.stringify(correctingEvent.extra_fields || {});
    const categoryChanged = correctionCategory !== correctingEvent.category;
    const eventTypeChanged = correctionEventType !== correctingEvent.event_type;
    const datetimeChanged = correctionDatetime !== toDateTimeLocalValue(correctingEvent.event_datetime);
    const finalAttachments = (correctingEvent.attachments || [])
      .filter((id) => !correctionRemovedAttachments.includes(id))
      .concat(correctionNewAttachments);
    const attachmentsChanged = correctionRemovedAttachments.length > 0 || correctionNewAttachments.length > 0;
    if (!descriptionChanged && !extraFieldsChanged && !categoryChanged && !eventTypeChanged && !datetimeChanged && !attachmentsChanged) {
      toast.error('Aucune modification détectée');
      return;
    }
    if (correctionNeedsPin && !correctionPinVerified) {
      toast.error('Le code personnel du rédacteur doit être saisi et confirmé');
      return;
    }
    setSubmittingCorrection(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${correctingEvent.id}/correct`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          new_description: correctionText.trim(),
          extra_fields: correctionExtraFields,
          category: correctionCategory,
          event_type: correctionEventType,
          event_datetime: correctionDatetime,
          attachments: finalAttachments,
          ...(correctionNeedsPin ? { redacteur_pin: correctionPin } : {})
        })
      });
      if (response.ok) {
        toast.success('Correction enregistrée, historique conservé');
        setCorrectingEvent(null);
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la correction');
        if (correctionNeedsPin) {
          setCorrectionPin('');
          setCorrectionPinVerified(false);
        }
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // Note : la suppression d'événements de la main courante est volontairement
  // impossible, y compris pour les administrateurs, conformément à l'obligation
  // légale d'intangibilité de la main courante électronique (Code de la Sécurité
  // Intérieure). Le serveur refuse également toute tentative de suppression.
  // Une correction tracée (avec historique complet) reste possible pour les
  // chefs de poste et administrateurs via handleSubmitCorrection ci-dessus.

  // Un événement en attente de validation (agent, saisie tardive) est
  // regroupé à part, hors de l'ordre chronologique, tant qu'un chef de poste
  // ne l'a pas validé - il rejoint ensuite sa place chronologique normale.
  const pendingValidationEvents = events.filter((e) => e.pending_validation);
  const chronologicalEvents = events.filter((e) => !e.pending_validation);

  const renderEventCard = (event) => (
    <Card key={event.id} className="card-hover glass-effect" data-testid={`event-${event.id}`}>
      <CardHeader className="p-3 pb-2">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <Badge className={`${categoryColors[event.category]} text-white text-xs`}>
                {event.category}
              </Badge>
              <Badge variant="outline" className="text-xs">{event.event_type}</Badge>
              {event.edit_history && event.edit_history.length > 0 && (
                <Badge
                  className="bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1 cursor-pointer text-xs"
                  onClick={() => setViewingHistory(event.id)}
                  data-testid={`view-history-${event.id}`}
                >
                  <History className="h-3 w-3" />
                  Corrigé
                </Badge>
              )}
              {event.attachments && event.attachments.length > 0 && (
                <Badge
                  className="bg-slate-500 hover:bg-slate-600 text-white flex items-center gap-1 cursor-pointer text-xs"
                  onClick={() => setViewingAttachments(event.id)}
                  data-testid={`view-attachments-${event.id}`}
                >
                  <Paperclip className="h-3 w-3" />
                  Pièce jointe
                </Badge>
              )}
              {event.is_important && (
                <Badge className="bg-red-600 text-white flex items-center gap-1 text-xs" data-testid={`important-badge-${event.id}`}>
                  <AlertTriangle className="h-3 w-3" />
                  Important
                </Badge>
              )}
              {event.pending_validation && (
                <Badge className="bg-amber-500 text-white flex items-center gap-1 text-xs" data-testid={`pending-validation-badge-${event.id}`}>
                  <Clock className="h-3 w-3" />
                  En attente de validation
                </Badge>
              )}
              {event.extra_fields?.auto_generated && (
                <Badge variant="outline" className="flex items-center gap-1 text-xs border-indigo-300 text-indigo-600 dark:text-indigo-400" data-testid={`auto-generated-badge-${event.id}`}>
                  <Clock className="h-3 w-3" />
                  Automatique
                </Badge>
              )}
            </div>
            <div className="space-y-0.5 text-xs text-gray-600 dark:text-slate-300">
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                <span>{event.redacteur_name ? `${event.user_name} — Rédigé par ${event.redacteur_name}` : event.user_name}</span>
              </div>
              <div
                className="flex items-center gap-1.5 cursor-pointer hover:underline w-fit"
                onClick={() => setViewingTimestampFor(event.id)}
                data-testid={`view-timestamp-${event.id}`}
              >
                <Calendar className="h-3.5 w-3.5" />
                <span>
                  {formatDateParis(event.event_datetime)}
                </span>
              </div>
            </div>
            {Object.keys(event.extra_fields || {}).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {Object.entries(event.extra_fields).filter(([key]) => !['materiel', 'auto_generated', 'source_prise_service_id', 'alert_time_1', 'alert_time_2'].includes(key)).map(([key, value]) => (
                  value !== '' && value !== null && value !== undefined && (
                    <span
                      key={key}
                      className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-1.5 py-0.5 rounded-md"
                    >
                      <span className="font-medium">{extraFieldLabels[key] || key} :</span> {formatExtraFieldValue(key, value)}
                    </span>
                  )
                ))}
              </div>
            )}
            {/* Horaires de rappel de non-restitution : visibles uniquement
                des chefs de poste et admins, pas des agents. */}
            {canMarkImportant && (event.extra_fields?.alert_time_1 || event.extra_fields?.alert_time_2) && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {event.extra_fields?.alert_time_1 && (
                  <span className="text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-md border border-amber-200 dark:border-amber-900">
                    <span className="font-medium">1er rappel :</span> {event.extra_fields.alert_time_1}
                  </span>
                )}
                {event.extra_fields?.alert_time_2 && (
                  <span className="text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-md border border-amber-200 dark:border-amber-900">
                    <span className="font-medium">2ème rappel :</span> {event.extra_fields.alert_time_2}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-1.5">
            {canMarkImportant && event.pending_validation && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2 border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                onClick={() => handleValidateEvent(event.id)}
                data-testid={`validate-event-${event.id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Valider
              </Button>
            )}
            {canMarkImportant && !event.is_important && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                onClick={() => handleMarkImportant(event.id)}
                data-testid={`mark-important-${event.id}`}
              >
                <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                Marquer important
              </Button>
            )}
            {canCorrectEvent(event) && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2"
                onClick={() => openCorrection(event)}
                data-testid={`correct-event-${event.id}`}
              >
                <Pencil className="h-3.5 w-3.5 mr-1" />
                Corriger
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-3 pt-0">
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-slate-200">Description</label>
          <p className="mt-0.5 text-sm text-gray-700 dark:text-slate-200"><FormattedText text={event.description} /></p>
        </div>

        {event.observations && event.observations.length > 0 && (
          <div className="space-y-1.5 pl-3 border-l-2 border-indigo-300 dark:border-indigo-700">
            {event.observations.map((obs) => (
              <div key={obs.id}>
                <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                  Observation de {obs.author_name} — {formatDateParis(obs.created_at)}
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-200 whitespace-pre-wrap">{obs.text}</p>
              </div>
            ))}
          </div>
        )}

        {canAddObservation && (
          addingObservationFor === event.id ? (
            <div className="space-y-1.5">
              <Textarea
                rows={2}
                placeholder="Ajouter une observation..."
                value={observationDrafts[event.id] || ''}
                onChange={(e) => setObservationDrafts(prev => ({ ...prev, [event.id]: e.target.value }))}
                data-testid={`observation-textarea-${event.id}`}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="h-7 text-xs btn-primary"
                  onClick={() => handleAddObservation(event.id)}
                  disabled={submittingObservationFor === event.id}
                  data-testid={`submit-observation-${event.id}`}
                >
                  {submittingObservationFor === event.id ? 'Ajout...' : 'Ajouter'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setAddingObservationFor(null)}
                >
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setAddingObservationFor(event.id)}
              data-testid={`add-observation-${event.id}`}
            >
              + Ajouter une observation
            </Button>
          )
        )}

        {event.signature_data && (
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-slate-200">Signature</label>
            <img src={event.signature_data} alt="Signature" className="mt-1 border rounded max-h-24" />
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-3 animate-fade-in" data-testid="main-courante">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-1" style={{ fontFamily: 'Space Grotesk' }}>
              Main Courante
            </h1>
            <p className="text-gray-600 dark:text-slate-300">Gestion des événements de sécurité</p>
          </div>
          {(user?.permission_overrides?.write_main_courante ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role)) && (
            <Dialog open={showForm} onOpenChange={setShowForm}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-event-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvel Événement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Créer un événement</DialogTitle>
                </DialogHeader>
                <EventForm sites={sites} user={user} onSuccess={handleEventCreated} onCancel={() => setShowForm(false)} />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Consigne(s) temporaire(s) en cours - un seul encadré, quel que soit le nombre */}
        {activeTemporaryConsignes.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (activeTemporaryConsignes.length === 1) {
                setViewingTemporaryConsigne(activeTemporaryConsignes[0]);
              } else {
                setShowingTemporaryList(true);
              }
            }}
            className="w-full text-left p-4 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 border-2 border-indigo-300 dark:border-indigo-700 hover:bg-indigo-200 dark:hover:bg-indigo-900/70 shadow-sm hover:shadow-md transition-all flex items-center gap-3"
            data-testid="active-events-banner"
          >
            <Info className="h-6 w-6 shrink-0 text-indigo-600 dark:text-indigo-400" />
            <div className="flex-1">
              <p className="font-bold text-sm text-indigo-800 dark:text-indigo-200">Consigne temporaire en cours</p>
              {activeTemporaryConsignes.length === 1 ? (
                <p className="text-sm text-indigo-700 dark:text-indigo-300">{activeTemporaryConsignes[0].title} — cliquez pour consulter la consigne</p>
              ) : (
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  {activeTemporaryConsignes.length} événements en cours sur votre site — cliquez pour consulter les consignes
                </p>
              )}
            </div>
          </button>
        )}

        {/* Rappel discret des événements du calendrier prévus aujourd'hui sur ce
            site - permet de les voir, avec leur description, sans avoir à
            ouvrir le calendrier. Cliquer sur un événement ouvre son détail. */}
        {todayCalendarEvents.length > 0 && (
          <div
            className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 text-xs flex-wrap"
            data-testid="today-calendar-events-banner"
          >
            <Calendar className="h-3.5 w-3.5 shrink-0 text-teal-600 dark:text-teal-400" />
            <span className="text-teal-700 dark:text-teal-300">Aujourd'hui :</span>
            {todayCalendarEvents.map((ev, idx) => (
              <React.Fragment key={ev.id}>
                {idx > 0 && <span className="text-teal-400">·</span>}
                <button
                  type="button"
                  onClick={() => setViewingCalendarEvent(ev)}
                  className="font-medium text-teal-800 dark:text-teal-200 hover:underline"
                  data-testid={`today-calendar-event-${ev.id}`}
                >
                  {ev.time ? `${ev.time} ` : ''}{ev.title}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Clé(s) perçue(s) non rendue(s) : jaune à partir de 17h, rouge à
            partir de 18h (voir GET /api/keys/pending-status) */}
        {keyAlertStatus && keyAlertStatus.alert_level !== 'none' && (
          <button
            type="button"
            onClick={() => setShowKeyAlertList(true)}
            className={`w-full text-left p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all flex items-center gap-3 ${
              keyAlertStatus.alert_level === 'red'
                ? 'bg-red-100 dark:bg-red-900/50 border-red-400 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/70'
                : 'bg-yellow-100 dark:bg-yellow-900/50 border-yellow-400 dark:border-yellow-700 hover:bg-yellow-200 dark:hover:bg-yellow-900/70'
            }`}
            data-testid="key-alert-banner"
          >
            <KeyRound className={`h-6 w-6 shrink-0 ${keyAlertStatus.alert_level === 'red' ? 'text-red-600 dark:text-red-400' : 'text-yellow-700 dark:text-yellow-400'}`} />
            <div className="flex-1">
              <p className={`font-bold text-sm ${keyAlertStatus.alert_level === 'red' ? 'text-red-800 dark:text-red-200' : 'text-yellow-800 dark:text-yellow-200'}`}>
                Clé(s) non rendue(s)
              </p>
              <p className={`text-sm ${keyAlertStatus.alert_level === 'red' ? 'text-red-700 dark:text-red-300' : 'text-yellow-800 dark:text-yellow-300'}`}>
                Les clés suivantes ({(keyAlertStatus.alerting_keys || []).map(k => k.extra_fields?.badge_number).join(', ')}) ont été perçues et toujours pas rendues — cliquez pour consulter
              </p>
            </div>
          </button>
        )}

        {/* Liste détaillée des clés non rendues, avec possibilité d'ajouter
            une observation (chefs de poste / admins) une fois le bandeau rouge */}
        <Dialog open={showKeyAlertList} onOpenChange={setShowKeyAlertList}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-red-600" />
                Clé(s) non rendue(s)
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {(keyAlertStatus?.alerting_keys || []).map((k) => (
                <div key={k.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2">
                  <p className="font-medium text-sm text-slate-800 dark:text-slate-100">Clé n° {k.extra_fields?.badge_number}</p>
                  <div className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                    <p>Société : {k.extra_fields?.company_name}</p>
                    <p>Technicien : {k.extra_fields?.technician_name}</p>
                    <p>Téléphone : {k.extra_fields?.phone_number}</p>
                    <p>Perçue le : {formatDateParis(k.event_datetime)}</p>
                  </div>
                  {k.observations && k.observations.length > 0 && (
                    <div className="space-y-1 pt-1 border-t border-slate-200 dark:border-slate-700">
                      {k.observations.map((o, idx) => (
                        <p key={idx} className="text-xs text-slate-600 dark:text-slate-300">
                          <span className="font-medium">{o.author_name}</span> ({formatDateParis(o.created_at)}) : {o.text}
                        </p>
                      ))}
                    </div>
                  )}
                  {keyAlertStatus.alert_level === 'red' && (user.role === 'admin' || user.role === 'chef_poste') && (
                    <div className="flex gap-2 pt-1">
                      <Input
                        placeholder="Ajouter une observation (ex: restitué à l'APS de nuit)"
                        value={keyObservationText[k.id] || ''}
                        onChange={(e) => setKeyObservationText((prev) => ({ ...prev, [k.id]: e.target.value }))}
                        className="text-xs"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAddKeyObservation(k.id)}
                        disabled={submittingKeyObservation === k.id}
                      >
                        Ajouter
                      </Button>
                    </div>
                  )}
                  {user.role === 'admin' && (
                    <div className="pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                        onClick={() => handleAcknowledgeForgottenKeyReturn(k.id)}
                        disabled={acknowledgingKeyReturn === k.id}
                        data-testid={`acknowledge-forgotten-return-${k.id}`}
                      >
                        {acknowledgingKeyReturn === k.id ? 'Enregistrement...' : 'Acquitter (retour oublié de saisie)'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Rappel quotidien (17h : jaune, 18h : rouge) tant qu'il n'a pas été
            validé pour ce palier, par un compte personnel ou de site */}
        <Dialog open={showKeyAlertPopup} onOpenChange={() => {}}>
          <DialogContent
            className="max-w-md [&>button]:hidden"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${keyAlertStatus?.alert_level === 'red' ? 'text-red-600 dark:text-red-400' : 'text-yellow-700 dark:text-yellow-400'}`}>
                <KeyRound className="h-5 w-5" />
                Clé(s) non rendue(s)
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
              <p>
                Les clés suivantes ({(keyAlertStatus?.alerting_keys || []).map(k => k.extra_fields?.badge_number).join(', ')}) ont été perçues et toujours pas rendues.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs">Qui valide cet avertissement ?</Label>
                <Select
                  value={keyPopupValidatorId}
                  onValueChange={(value) => { setKeyPopupValidatorId(value); setKeyPopupPin(''); setKeyPopupPinError(''); }}
                >
                  <SelectTrigger data-testid="key-alert-validator-select">
                    <SelectValue placeholder="Sélectionnez votre nom" />
                  </SelectTrigger>
                  <SelectContent>
                    {keySiteAgents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {keyPopupValidatorId && (
                <div className="flex flex-col items-center gap-2 pt-1">
                  <Label className="text-xs self-start">Votre code personnel</Label>
                  <InputOTP
                    maxLength={4}
                    inputMode="numeric"
                    value={keyPopupPin}
                    onChange={(value) => { setKeyPopupPin(value); setKeyPopupPinError(''); }}
                    onComplete={(value) => handleAckKeyPopup(value)}
                    disabled={keyPopupPinChecking}
                    data-testid="key-alert-validator-pin-otp"
                    autoFocus
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} mask />
                      <InputOTPSlot index={1} mask />
                      <InputOTPSlot index={2} mask />
                      <InputOTPSlot index={3} mask />
                    </InputOTPGroup>
                  </InputOTP>
                  {keyPopupPinChecking && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>
                  )}
                  {keyPopupPinError && (
                    <div
                      className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full"
                      data-testid="key-alert-validator-pin-error"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{keyPopupPinError}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Liste des consignes temporaires actives, quand il y en a plusieurs */}
        <Dialog open={showingTemporaryList} onOpenChange={setShowingTemporaryList}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-indigo-600" />
                Consignes temporaires en cours
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              {activeTemporaryConsignes.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setShowingTemporaryList(false); setViewingTemporaryConsigne(c); }}
                  className="w-full text-left p-3 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors"
                  data-testid={`temporary-list-item-${c.id}`}
                >
                  <p className="font-medium text-sm text-slate-800 dark:text-slate-100">{c.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Du {c.start_date?.split('-').reverse().join('/')}{c.start_time ? ` ${c.start_time}` : ''} au {c.end_date?.split('-').reverse().join('/')}{c.end_time ? ` ${c.end_time}` : ''}
                  </p>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Détail d'un événement du calendrier signalé sur la main courante -
            ouvert en cliquant sur son titre dans l'encadré du jour. */}
        <Dialog open={!!viewingCalendarEvent} onOpenChange={(open) => !open && setViewingCalendarEvent(null)}>
          <DialogContent className="max-w-lg">
            {viewingCalendarEvent && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-600" />
                    {viewingCalendarEvent.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">
                      {viewingCalendarEvent.date?.split('-').reverse().join('/')}{viewingCalendarEvent.time ? ` à ${viewingCalendarEvent.time}` : ''}
                    </Badge>
                    {viewingCalendarEvent.site_id && (
                      <Badge variant="outline">{sites.find((s) => s.id === viewingCalendarEvent.site_id)?.name || 'Site inconnu'}</Badge>
                    )}
                  </div>
                  {viewingCalendarEvent.description ? (
                    <p className="text-gray-700 dark:text-slate-200 whitespace-pre-wrap">{viewingCalendarEvent.description}</p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-slate-500 italic">Aucune description</p>
                  )}
                  <p className="text-xs text-slate-400 dark:text-slate-500">Ajouté par {viewingCalendarEvent.redacteur_name || viewingCalendarEvent.created_by_name}</p>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Pop-up dédié à la consigne temporaire en cours, pour ne pas la
            mélanger avec l'ensemble des consignes du site */}
        <Dialog open={!!viewingTemporaryConsigne} onOpenChange={(open) => !open && setViewingTemporaryConsigne(null)}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
            {viewingTemporaryConsigne && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5 text-indigo-600" />
                    {viewingTemporaryConsigne.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">{viewingTemporaryConsigne.category}</Badge>
                    <Badge className="bg-indigo-500 text-white">
                      Du {viewingTemporaryConsigne.start_date?.split('-').reverse().join('/')}{viewingTemporaryConsigne.start_time ? ` ${viewingTemporaryConsigne.start_time}` : ''} au {viewingTemporaryConsigne.end_date?.split('-').reverse().join('/')}{viewingTemporaryConsigne.end_time ? ` ${viewingTemporaryConsigne.end_time}` : ''}
                    </Badge>
                  </div>
                  <p className="text-gray-700 dark:text-slate-200 whitespace-pre-wrap">{viewingTemporaryConsigne.content}</p>
                  {viewingTemporaryConsigne.attachments && viewingTemporaryConsigne.attachments.length > 0 && (
                    <FileViewer fileIds={viewingTemporaryConsigne.attachments} />
                  )}
                  {viewingTemporaryConsigne.requires_signature && (
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                      {viewingTemporaryConsigne.signatures?.some(s => s.user_id === user.id) ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Signée ✓</Badge>
                      ) : (
                        (user?.permission_overrides?.sign_consignes ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role)) && (
                          <Button
                            size="sm"
                            className="btn-primary"
                            onClick={() => setSigningTemporaryConsigne(viewingTemporaryConsigne)}
                            data-testid="sign-temporary-consigne-btn"
                          >
                            Signer cette consigne
                          </Button>
                        )
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Sur un compte de site, l'agent signataire doit d'abord être choisi
            et confirmer son identité avec son code personnel. */}
        {signingTemporaryConsigne && isSiteAccount && (
          <Dialog open={true} onOpenChange={(open) => !open && setSigningTemporaryConsigne(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Qui signe cette consigne ?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Agent signataire</Label>
                  <Select
                    value={consigneSignerId}
                    onValueChange={(value) => {
                      setConsigneSignerId(value);
                      setConsigneSignerPin('');
                      setConsigneSignerPinVerified(false);
                      setConsigneSignerPinError('');
                      setConsigneSignerPinDialogOpen(true);
                    }}
                  >
                    <SelectTrigger data-testid="temp-consigne-signer-select">
                      <SelectValue placeholder="Sélectionnez un agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {consigneSignerAgents
                        .filter((a) => !signingTemporaryConsigne.signatures?.some((s) => s.user_id === a.id))
                        .map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {consigneSignerAgents.length > 0 && consigneSignerAgents.every((a) => signingTemporaryConsigne.signatures?.some((s) => s.user_id === a.id)) && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Tous les agents de ce site ont déjà signé cette consigne.</p>
                  )}
                </div>

                {consigneSignerId && (
                  <div>
                    {consigneSignerPinVerified ? (
                      <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        <span>Identité confirmée</span>
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          onClick={() => { setConsigneSignerPin(''); setConsigneSignerPinVerified(false); setConsigneSignerPinError(''); setConsigneSignerPinDialogOpen(true); }}
                        >
                          Modifier
                        </Button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" size="sm" onClick={() => setConsigneSignerPinDialogOpen(true)}>
                        Saisir le code personnel de l'agent *
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Popup de saisie du code personnel de l'agent signataire */}
        <Dialog open={consigneSignerPinDialogOpen} onOpenChange={(open) => { if (!consigneSignerPinChecking) setConsigneSignerPinDialogOpen(open); }}>
          <DialogContent className="max-w-xs">
            <DialogHeader>
              <DialogTitle className="text-base">Code personnel</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Demandé à {consigneSignerAgents.find(a => a.id === consigneSignerId)?.name || "l'agent sélectionné"} pour confirmer son identité.
            </p>
            <div className="flex flex-col items-center gap-2 py-2">
              <InputOTP
                maxLength={4}
                inputMode="numeric"
                value={consigneSignerPin}
                onChange={(value) => { setConsigneSignerPin(value); setConsigneSignerPinError(''); }}
                onComplete={(value) => verifyConsigneSignerPin(value, consigneSignerId)}
                disabled={consigneSignerPinChecking}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} mask />
                  <InputOTPSlot index={1} mask />
                  <InputOTPSlot index={2} mask />
                  <InputOTPSlot index={3} mask />
                </InputOTPGroup>
              </InputOTP>
              {consigneSignerPinChecking && <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>}
              {consigneSignerPinError && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{consigneSignerPinError}</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {signingTemporaryConsigne && (!isSiteAccount || consigneSignerPinVerified) && (
          <SignatureDialog
            open={!!signingTemporaryConsigne}
            onClose={() => setSigningTemporaryConsigne(null)}
            onSign={(sig) => handleSignTemporaryConsigne(sig)}
            title="Signature de la consigne"
          />
        )}

        {/* Période */}
        <Card className="glass-effect">
          <CardContent className="pt-4 pb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 block">Période</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'today', label: "Aujourd'hui" },
                { key: 'yesterday', label: 'Hier' },
                { key: 'week', label: 'Cette semaine' },
                { key: 'custom', label: 'Période personnalisée' },
              ].map(opt => (
                <Button
                  key={opt.key}
                  type="button"
                  variant={periodFilter === opt.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPeriodFilter(opt.key)}
                  className={periodFilter === opt.key ? 'bg-gradient-to-r from-indigo-600 to-purple-700 text-white hover:from-indigo-600 hover:to-purple-700' : ''}
                  data-testid={`period-${opt.key}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            {periodFilter === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="w-auto" data-testid="custom-start-date" />
                <span className="text-sm text-gray-500 dark:text-slate-400">à</span>
                <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="w-auto" data-testid="custom-end-date" />
                {canExportExcel && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!customStart || !customEnd || exportingExcel}
                    onClick={handleExportExcel}
                    className="ml-auto"
                    data-testid="export-excel-custom-period"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportingExcel ? 'Export en cours...' : 'Export Excel'}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="glass-effect">
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 block">Site *</label>
                <Select value={selectedSite} onValueChange={setSelectedSite}>
                  <SelectTrigger data-testid="filter-site">
                    <SelectValue placeholder="Sélectionnez un site" />
                  </SelectTrigger>
                  <SelectContent>
                    {user?.role === 'admin' && <SelectItem value="all">Tous les sites</SelectItem>}
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedSite && selectedSite !== 'all' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 block">Catégorie</label>
                  <Select
                    value={selectedCategory}
                    onValueChange={(value) => { setSelectedCategory(value); setSelectedEventType('all'); }}
                  >
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue placeholder="Toutes les catégories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {selectedSite && selectedSite !== 'all' && selectedCategory !== 'all' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 block">Sous-catégorie (type)</label>
                  <Select value={selectedEventType} onValueChange={setSelectedEventType}>
                    <SelectTrigger data-testid="filter-event-type">
                      <SelectValue placeholder="Toutes les sous-catégories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les sous-catégories</SelectItem>
                      {(EVENT_TYPE_CATEGORIES.find((c) => c.value === selectedCategory)?.types || []).map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Events List */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : events.length === 0 ? (
            <Card className="glass-effect">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-slate-300">
                  {periodFilter === 'custom' && (!customStart || !customEnd)
                    ? 'Sélectionnez une date de début et de fin pour afficher les événements'
                    : 'Aucun événement trouvé pour cette période'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {pendingValidationEvents.length > 0 && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500 text-white">{pendingValidationEvents.length}</Badge>
                    <h3 className="text-sm font-bold text-amber-700 dark:text-amber-400" style={{ fontFamily: 'Space Grotesk' }}>
                      En attente de validation par un chef de poste
                    </h3>
                  </div>
                  {pendingValidationEvents.map(renderEventCard)}
                </div>
              )}
              {chronologicalEvents.map(renderEventCard)}
            </>
          )}
        </div>

        {/* Fenêtre d'aperçu des pièces jointes, ouverte depuis le badge "Pièce jointe" */}
        <Dialog open={!!viewingAttachments} onOpenChange={(open) => !open && setViewingAttachments(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Pièces jointes</DialogTitle>
            </DialogHeader>
            {viewingAttachments && (
              <FileViewer
                fileIds={events.find(e => e.id === viewingAttachments)?.attachments || []}
                canDelete={user?.permission_overrides?.delete_event_attachments ?? user?.role === 'admin'}
                onDelete={(fileId) => handleDeleteAttachment(viewingAttachments, fileId)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Horodatage détaillé, ouvert en cliquant sur la date/heure d'un
            événement : distingue le moment réel de la saisie (created_at) de
            la date/heure programmée/déclarée pour l'événement (event_datetime) -
            utile pour repérer une saisie tardive ou antidatée. */}
        <Dialog open={!!viewingTimestampFor} onOpenChange={(open) => !open && setViewingTimestampFor(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horodatage
              </DialogTitle>
            </DialogHeader>
            {(() => {
              const ev = events.find(e => e.id === viewingTimestampFor);
              if (!ev) return null;
              return (
                <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
                  <p>
                    Événement généré le <span className="font-medium">{formatDateOnlyParis(ev.created_at)}</span> à{' '}
                    <span className="font-medium">{formatTimeParis(ev.created_at)}</span>
                  </p>
                  <p className="text-slate-500 dark:text-slate-400">
                    Programmé le <span className="font-medium">{formatDateOnlyParis(ev.event_datetime)}</span> à{' '}
                    <span className="font-medium">{formatTimeParis(ev.event_datetime)}</span>
                  </p>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Fenêtre de l'historique des corrections, ouverte depuis le badge "Corrigé" */}
        <Dialog open={!!viewingHistory} onOpenChange={(open) => !open && setViewingHistory(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Historique des corrections
              </DialogTitle>
            </DialogHeader>
            {viewingHistory && (() => {
              const history = events.find(e => e.id === viewingHistory)?.edit_history || [];
              const total = history.length;
              // Plus récent en premier, mais numéroté selon l'ordre réel des corrections.
              return (
                <div className="space-y-3">
                  {[...history].reverse().map((edit, reversedIdx) => {
                    const correctionNumber = total - reversedIdx;
                    // Format récent : liste détaillée des champs modifiés. Ancien format
                    // (avant l'ajout de "changes") : uniquement la description.
                    const fieldChanges = edit.changes && edit.changes.length > 0
                      ? edit.changes
                      : [{ field: 'description', old_value: edit.old_description, new_value: edit.new_description }];
                    return (
                      <div key={reversedIdx} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            <span className="font-semibold text-slate-700 dark:text-slate-200">Correction n°{correctionNumber}</span>
                            {' '}— <span className="font-medium">{edit.edited_by_name}</span>, le {formatDateParis(edit.edited_at)}
                          </p>
                          {total > 1 && correctionNumber === total && (
                            <Badge variant="outline" className="text-[10px]">Plus récente</Badge>
                          )}
                        </div>
                        <div className="space-y-2.5">
                          {fieldChanges.map((change, cIdx) => (
                            <div key={cIdx}>
                              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">
                                {change.field === 'description' ? 'Description' : (extraFieldLabels[change.field] || change.field)}
                              </p>
                              <p className="text-sm mb-0.5">
                                <span className="text-xs font-medium text-red-600 dark:text-red-400 uppercase mr-2">Avant</span>
                                <span className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-1 rounded">
                                  {(change.old_value ?? '') !== '' ? formatExtraFieldValue(change.field, change.old_value) : <em className="text-slate-400 not-italic">Vide</em>}
                                </span>
                              </p>
                              <p className="text-sm">
                                <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase mr-2">Après</span>
                                <span className="text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/40 px-1 rounded">
                                  {(change.new_value ?? '') !== '' ? formatExtraFieldValue(change.field, change.new_value) : <em className="text-slate-400 not-italic">Vide</em>}
                                </span>
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>

      {/* Fenêtre de correction d'une erreur de saisie */}
      <Dialog open={!!correctingEvent} onOpenChange={(open) => !open && setCorrectingEvent(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Corriger une erreur de saisie</DialogTitle>
          </DialogHeader>
          {correctingEvent && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-slate-300">
                La version précédente de chaque champ modifié restera visible dans l'historique de l'événement.
                Seul le site reste définitivement figé.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Catégorie</label>
                  <Select
                    value={correctionCategory}
                    onValueChange={(value) => {
                      setCorrectionCategory(value);
                      // Le type et les champs complementaires ne sont conserves que
                      // s'ils existent encore dans la nouvelle categorie, sinon on
                      // force une nouvelle selection plutot que de garder une
                      // combinaison incoherente.
                      const newTypes = EVENT_TYPE_CATEGORIES.find(c => c.value === value)?.types || [];
                      if (!newTypes.includes(correctionEventType)) {
                        setCorrectionEventType('');
                        setCorrectionExtraFields({});
                      } else if (correctionEventType === 'Alarme' && ['Sûreté', 'Incendie'].includes(value)) {
                        // Le type "Alarme" existe dans les deux categories, mais le
                        // sous-type choisi peut ne plus etre valide (ex: "Declenchement"
                        // n'existe que pour Incendie).
                        const validSubtypes = getAlarmSubtypes(value);
                        if (!validSubtypes.includes(correctionExtraFields.alarm_subtype)) {
                          setCorrectionExtraFields(prev => ({ ...prev, alarm_subtype: '' }));
                        }
                      }
                    }}
                  >
                    <SelectTrigger data-testid="correction-category">
                      <SelectValue placeholder="Sélectionnez une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Type d'événement</label>
                  <Select
                    value={correctionEventType}
                    onValueChange={(value) => {
                      setCorrectionEventType(value);
                      // Un sous-type deja choisi (alarme, secours...) peut ne plus etre
                      // pertinent pour le nouveau type : on repart d'extra_fields propre.
                      setCorrectionExtraFields({});
                    }}
                    disabled={!correctionSelectedCategory}
                  >
                    <SelectTrigger data-testid="correction-event-type">
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(correctionSelectedCategory?.types || []).map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Date et heure</label>
                  <Input
                    type="datetime-local"
                    value={correctionDatetime}
                    onChange={(e) => setCorrectionDatetime(e.target.value)}
                    max={user?.role === 'admin' ? undefined : getParisDateTimeLocal(5)}
                    data-testid="correction-datetime"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 block">Version actuelle</label>
                <p className="text-sm p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-gray-500 dark:text-slate-400">
                  <FormattedText text={correctingEvent.description} />
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-200">Nouvelle version *</label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 font-bold"
                      onClick={() => wrapTextareaSelection(correctionTextareaRef, correctionText, setCorrectionText, '**')}
                      title="Gras"
                      data-testid="correction-bold-btn"
                    >
                      G
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 underline"
                      onClick={() => wrapTextareaSelection(correctionTextareaRef, correctionText, setCorrectionText, '__')}
                      title="Souligné"
                      data-testid="correction-underline-btn"
                    >
                      S
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 italic"
                      onClick={() => wrapTextareaSelection(correctionTextareaRef, correctionText, setCorrectionText, '*')}
                      title="Italique"
                      data-testid="correction-italic-btn"
                    >
                      I
                    </Button>
                  </div>
                </div>
                <Textarea
                  ref={correctionTextareaRef}
                  rows={5}
                  value={correctionText}
                  onChange={(e) => setCorrectionText(e.target.value)}
                  data-testid="correction-textarea"
                />
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  Sélectionnez du texte puis cliquez sur G (gras), S (souligné) ou I (italique). Les retours à la ligne sont conservés.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 block">Pièces jointes</label>
                {(correctingEvent.attachments || []).filter((id) => !correctionRemovedAttachments.includes(id)).length > 0 && (
                  <FileViewer
                    fileIds={(correctingEvent.attachments || []).filter((id) => !correctionRemovedAttachments.includes(id))}
                    canDelete
                    onDelete={(fileId) => { setCorrectionRemovedAttachments((prev) => [...prev, fileId]); return true; }}
                  />
                )}
                <div className="mt-2">
                  <FileUpload
                    existingFiles={[]}
                    onFilesUploaded={(fileIds) => setCorrectionNewAttachments(fileIds)}
                  />
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  Retirez une photo mal attachée avec l'icône corbeille, ou ajoutez-en une oubliée. L'historique conserve toujours la liste avant/après.
                </p>
              </div>

              {(correctionIsApprehension || correctionIsBadgeOrKey || correctionIsAlarm || correctionIsSecours) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg">
                  {correctionIsAlarm && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Sous-type *</label>
                      <Select
                        value={correctionExtraFields.alarm_subtype || ''}
                        onValueChange={(value) => setCorrectionExtraFields(prev => ({ ...prev, alarm_subtype: value }))}
                      >
                        <SelectTrigger data-testid="correction-alarm-subtype">
                          <SelectValue placeholder="Sélectionnez un sous-type" />
                        </SelectTrigger>
                        <SelectContent>
                          {getAlarmSubtypes(correctionCategory).map((subtype) => (
                            <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {correctionIsSecours && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Sous-type *</label>
                      <Select
                        value={correctionExtraFields.secours_subtype || ''}
                        onValueChange={(value) => setCorrectionExtraFields(prev => ({ ...prev, secours_subtype: value }))}
                      >
                        <SelectTrigger data-testid="correction-secours-subtype">
                          <SelectValue placeholder="Sélectionnez un sous-type" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECOURS_SUBTYPES.map((subtype) => (
                            <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {correctionIsApprehension && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">N° d'appréhension</label>
                        <Input
                          value={correctionExtraFields.apprehension_number || ''}
                          onChange={(e) => setCorrectionExtraFields(prev => ({ ...prev, apprehension_number: e.target.value }))}
                          data-testid="correction-apprehension-number"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Montant</label>
                        <Input
                          type="number"
                          value={correctionExtraFields.amount || ''}
                          onChange={(e) => setCorrectionExtraFields(prev => ({ ...prev, amount: e.target.value }))}
                          data-testid="correction-amount"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Nombre d'articles</label>
                        <Input
                          type="number"
                          value={correctionExtraFields.article_count || ''}
                          onChange={(e) => setCorrectionExtraFields(prev => ({ ...prev, article_count: e.target.value }))}
                          data-testid="correction-article-count"
                        />
                      </div>
                    </>
                  )}
                  {correctionIsBadgeOrKey && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">Numéro de badge/clé</label>
                      <Input
                        value={correctionExtraFields.badge_number || ''}
                        onChange={(e) => setCorrectionExtraFields(prev => ({ ...prev, badge_number: e.target.value }))}
                        data-testid="correction-badge-number"
                      />
                    </div>
                  )}
                  {correctionIsKeyDeparture && (
                    <div className="md:col-span-2 space-y-3 pt-2 border-t border-blue-100 dark:border-slate-700">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={!!correctionExtraFields.departure_final}
                          onCheckedChange={(checked) => setCorrectionExtraFields(prev => ({
                            ...prev,
                            departure_final: checked,
                            ...(checked ? { alert_time_1: '', alert_time_2: '' } : {})
                          }))}
                          data-testid="correction-key-departure-final"
                        />
                        Départ définitif (aucun retour attendu, pas de rappel)
                      </label>
                      {!correctionExtraFields.departure_final && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">1ᵉʳ rappel de non-restitution</label>
                            <Input
                              type="time"
                              value={correctionExtraFields.alert_time_1 || ''}
                              onChange={(e) => setCorrectionExtraFields(prev => ({ ...prev, alert_time_1: e.target.value }))}
                              data-testid="correction-key-alert-time-1"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-200 block">2ᵉ rappel de non-restitution</label>
                            <Input
                              type="time"
                              value={correctionExtraFields.alert_time_2 || ''}
                              onChange={(e) => setCorrectionExtraFields(prev => ({ ...prev, alert_time_2: e.target.value }))}
                              data-testid="correction-key-alert-time-2"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {correctionNeedsPin && (
                <div>
                  {correctionPinVerified ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      <span>Code personnel confirmé</span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => { setCorrectionPin(''); setCorrectionPinVerified(false); setCorrectionPinError(''); setCorrectionPinDialogOpen(true); }}
                        data-testid="correction-pin-edit"
                      >
                        Modifier
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setCorrectionPinDialogOpen(true)}
                      data-testid="correction-pin-open-btn"
                    >
                      <KeyRound className="h-4 w-4" />
                      Saisir le code personnel du rédacteur *
                    </Button>
                  )}
                </div>
              )}

              <Button
                onClick={handleSubmitCorrection}
                disabled={submittingCorrection || (correctionNeedsPin && !correctionPinVerified)}
                className="w-full btn-primary"
                data-testid="submit-correction-btn"
              >
                {submittingCorrection ? 'Enregistrement...' : 'Enregistrer la correction'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Popup de saisie du code personnel du rédacteur, pour confirmer une
          correction : vérifiée en direct dès les 4 chiffres saisis. */}
      <Dialog open={correctionPinDialogOpen} onOpenChange={(open) => { if (!correctionPinChecking) setCorrectionPinDialogOpen(open); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4 text-indigo-600" />
              Code personnel
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Demandé à {correctingEvent?.redacteur_name || 'l\'agent rédacteur'} pour confirmer cette correction.
          </p>
          <div className="flex flex-col items-center gap-2 py-2">
            <InputOTP
              maxLength={4}
              inputMode="numeric"
              value={correctionPin}
              onChange={(value) => { setCorrectionPin(value); setCorrectionPinError(''); }}
              onComplete={(value) => verifyCorrectionPin(value)}
              disabled={correctionPinChecking}
              data-testid="correction-pin-otp"
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} mask />
                <InputOTPSlot index={1} mask />
                <InputOTPSlot index={2} mask />
                <InputOTPSlot index={3} mask />
              </InputOTPGroup>
            </InputOTP>

            {correctionPinChecking && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>
            )}

            {correctionPinError && (
              <div
                className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full"
                data-testid="correction-pin-error"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{correctionPinError}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MainCourante;
